import { useState } from 'react';
import { BellRing } from 'lucide-react';

import { disablePush, enablePush, usePushState } from '@/lib/push';
import { t } from '@/lib/i18n';

/** Ajuste completo: interruptor con explicación de cada caso. */
export function PushToggle() {
  const state = usePushState();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (on) await enablePush();
      else await disablePush();
    } catch {
      setError(t('No se han podido activar. Vuelve a intentarlo.'));
    } finally {
      setBusy(false);
    }
  };

  const hint =
    state === 'needs-install'
      ? t('En iPhone y iPad, primero añade UAChess a la pantalla de inicio (Compartir → Añadir a inicio) y ábrela desde ahí.')
      : state === 'denied'
        ? t('Los has bloqueado en el navegador. Permite las notificaciones de este sitio desde los ajustes del navegador.')
        : state === 'unsupported'
          ? t('Este navegador no admite avisos.')
          : t('Te avisamos cuando te toca en una partida por correspondencia, te retan, empieza tu torneo o te escriben. Solo en este dispositivo.');

  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 border-b border-border py-2.5 hover:bg-elevated/40 px-2 rounded transition-colors">
      <span className="min-w-0">
        <span className="block text-sm">{t('Avisos en este dispositivo')}</span>
        <span className="block text-xs text-faint">{hint}</span>
        {error ? <span className="block text-xs text-danger">{error}</span> : null}
      </span>
      <input
        type="checkbox"
        className="mt-1 size-4 shrink-0 accent-[var(--color-brand)]"
        checked={state === 'on'}
        disabled={busy || (state !== 'on' && state !== 'off')}
        onChange={(event) => void toggle(event.target.checked)}
      />
    </label>
  );
}

/** Invitación corta al pie de la campana, solo si aún no están activados. */
export function PushInvite() {
  const state = usePushState();
  const [busy, setBusy] = useState(false);

  if (state !== 'off') return null;

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void enablePush()
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
      className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2.5 text-left text-sm text-brand transition-colors hover:bg-hover"
    >
      <BellRing size={16} className="shrink-0" />
      <span>{t('Recibir estos avisos en el móvil')}</span>
    </button>
  );
}
